import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query'
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios'
import type {
  CreatePoll201,
  CreatePoll400,
  CreatePoll401,
  CreatePollBody,
  CreateTweet202,
  CreateTweet400,
  CreateTweet401,
  CreateTweet500,
  CreateTweetBody,
  DeleteTweet200,
  GetHashtagByTag200,
  GetHashtagByTag404,
  GetHashtagByTagParams,
  GetHashtags200,
  GetHashtagsParams,
  GetPollResults200,
  GetPollResults404,
  GetTweetById200,
  GetTweets200,
  GetTweets400,
  GetTweets401,
  GetTweets500,
  GetTweetsParams,
  IncrementTweetView200,
  IncrementTweetView400,
  IncrementTweetView401,
  IncrementTweetView404,
  LikeTweet200,
  Retweet200,
  UndoRetweet200,
  UnlikeTweet200,
  VoteOnPoll200,
  VoteOnPoll400,
  VoteOnPoll401,
  VoteOnPoll403,
  VoteOnPoll404,
  VoteOnPollBody
} from './types'


type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;



/**
 * 새로운 트윗을 작성합니다. 텍스트와 미디어 파일을 함께 업로드할 수 있습니다.
 * @summary 트윗 작성
 */
export const createTweet = (
    createTweetBody: CreateTweetBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<CreateTweet202>> => {const formData = new FormData();
if(createTweetBody.jsonData !== undefined) {
 formData.append('jsonData', JSON.stringify(createTweetBody.jsonData));
 }
if(createTweetBody.mediaFiles !== undefined) {
 createTweetBody.mediaFiles.forEach(value => formData.append('mediaFiles', value));
 }

    
    return axios.post(
      `https://api.ugot.uk/api/tweets`,
      formData,options
    );
  }



export const getCreateTweetMutationOptions = <TError = AxiosError<CreateTweet400 | CreateTweet401 | CreateTweet500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTweet>>, TError,{data: CreateTweetBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof createTweet>>, TError,{data: CreateTweetBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTweet>>, {data: CreateTweetBody}> = (props) => {
          const {data} = props ?? {};

          return  createTweet(data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreateTweetMutationResult = NonNullable<Awaited<ReturnType<typeof createTweet>>>
    export type CreateTweetMutationBody = CreateTweetBody
    export type CreateTweetMutationError = AxiosError<CreateTweet400 | CreateTweet401 | CreateTweet500>

    /**
 * @summary 트윗 작성
 */
export const useCreateTweet = <TError = AxiosError<CreateTweet400 | CreateTweet401 | CreateTweet500>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTweet>>, TError,{data: CreateTweetBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof createTweet>>,
        TError,
        {data: CreateTweetBody},
        TContext
      > => {

      const mutationOptions = getCreateTweetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 다양한 조건으로 트윗을 조회합니다. 검색 조건 없이 호출할 경우 최신 트윗 목록을 조회합니다.

## 주요 조건
- **type**: 트윗 타입에 따른 필터링
- **query**: 키워드 검색
- **hashtag**: 해시태그 검색
- **userId**: 특정 사용자의 트윗 조회
- **startDate/endDate**: 날짜 범위 지정

## 정렬 옵션
- **sortBy**: 정렬 기준 (createdAt, likeCount, retweetCount, viewCount)
- **sortDirection**: 정렬 방향 (ASC, DESC)

 * @summary 트윗 목록 조회
 */
export const getTweets = (
    params?: GetTweetsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetTweets200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/tweets`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetTweetsQueryKey = (params?: GetTweetsParams,) => {
    return [`https://api.ugot.uk/api/tweets`, ...(params ? [params]: [])] as const;
    }

    
export const getGetTweetsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getTweets>>, TError = AxiosError<GetTweets400 | GetTweets401 | GetTweets500>>(params?: GetTweetsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getTweets>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTweetsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTweets>>> = ({ signal }) => getTweets(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getTweets>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTweetsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getTweets>>>
export type GetTweetsInfiniteQueryError = AxiosError<GetTweets400 | GetTweets401 | GetTweets500>

/**
 * @summary 트윗 목록 조회
 */
export const useGetTweetsInfinite = <TData = Awaited<ReturnType<typeof getTweets>>, TError = AxiosError<GetTweets400 | GetTweets401 | GetTweets500>>(
 params?: GetTweetsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getTweets>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetTweetsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetTweetsQueryOptions = <TData = Awaited<ReturnType<typeof getTweets>>, TError = AxiosError<GetTweets400 | GetTweets401 | GetTweets500>>(params?: GetTweetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTweets>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTweetsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTweets>>> = ({ signal }) => getTweets(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTweets>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTweetsQueryResult = NonNullable<Awaited<ReturnType<typeof getTweets>>>
export type GetTweetsQueryError = AxiosError<GetTweets400 | GetTweets401 | GetTweets500>

/**
 * @summary 트윗 목록 조회
 */
export const useGetTweets = <TData = Awaited<ReturnType<typeof getTweets>>, TError = AxiosError<GetTweets400 | GetTweets401 | GetTweets500>>(
 params?: GetTweetsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTweets>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetTweetsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * @summary 트윗 상세 조회
 */
export const getTweetById = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetTweetById200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/tweets/${id}`,options
    );
  }


export const getGetTweetByIdQueryKey = (id: number,) => {
    return [`https://api.ugot.uk/api/tweets/${id}`] as const;
    }

    
export const getGetTweetByIdInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getTweetById>>, TError = AxiosError<unknown>>(id: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getTweetById>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTweetByIdQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTweetById>>> = ({ signal }) => getTweetById(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getTweetById>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTweetByIdInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getTweetById>>>
export type GetTweetByIdInfiniteQueryError = AxiosError<unknown>

/**
 * @summary 트윗 상세 조회
 */
export const useGetTweetByIdInfinite = <TData = Awaited<ReturnType<typeof getTweetById>>, TError = AxiosError<unknown>>(
 id: number, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getTweetById>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetTweetByIdInfiniteQueryOptions(id,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetTweetByIdQueryOptions = <TData = Awaited<ReturnType<typeof getTweetById>>, TError = AxiosError<unknown>>(id: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTweetById>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTweetByIdQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTweetById>>> = ({ signal }) => getTweetById(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTweetById>>, TError, TData> & { queryKey: QueryKey }
}

export type GetTweetByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getTweetById>>>
export type GetTweetByIdQueryError = AxiosError<unknown>

/**
 * @summary 트윗 상세 조회
 */
export const useGetTweetById = <TData = Awaited<ReturnType<typeof getTweetById>>, TError = AxiosError<unknown>>(
 id: number, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getTweetById>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetTweetByIdQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * @summary 트윗 삭제
 */
export const deleteTweet = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<DeleteTweet200>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/tweets/${id}`,options
    );
  }



export const getDeleteTweetMutationOptions = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof deleteTweet>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteTweet>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  deleteTweet(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type DeleteTweetMutationResult = NonNullable<Awaited<ReturnType<typeof deleteTweet>>>
    
    export type DeleteTweetMutationError = AxiosError<unknown>

    /**
 * @summary 트윗 삭제
 */
export const useDeleteTweet = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteTweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof deleteTweet>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getDeleteTweetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 트윗 좋아요
 */
export const likeTweet = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<LikeTweet200>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/tweets/${id}/like`,undefined,options
    );
  }



export const getLikeTweetMutationOptions = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof likeTweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof likeTweet>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof likeTweet>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  likeTweet(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type LikeTweetMutationResult = NonNullable<Awaited<ReturnType<typeof likeTweet>>>
    
    export type LikeTweetMutationError = AxiosError<unknown>

    /**
 * @summary 트윗 좋아요
 */
export const useLikeTweet = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof likeTweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof likeTweet>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getLikeTweetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 트윗 좋아요 취소
 */
export const unlikeTweet = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UnlikeTweet200>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/tweets/${id}/like`,options
    );
  }



export const getUnlikeTweetMutationOptions = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlikeTweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof unlikeTweet>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unlikeTweet>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  unlikeTweet(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UnlikeTweetMutationResult = NonNullable<Awaited<ReturnType<typeof unlikeTweet>>>
    
    export type UnlikeTweetMutationError = AxiosError<unknown>

    /**
 * @summary 트윗 좋아요 취소
 */
export const useUnlikeTweet = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlikeTweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof unlikeTweet>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getUnlikeTweetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 리트윗하기
 */
export const retweet = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<Retweet200>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/tweets/${id}/retweet`,undefined,options
    );
  }



export const getRetweetMutationOptions = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof retweet>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retweet>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  retweet(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type RetweetMutationResult = NonNullable<Awaited<ReturnType<typeof retweet>>>
    
    export type RetweetMutationError = AxiosError<unknown>

    /**
 * @summary 리트윗하기
 */
export const useRetweet = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof retweet>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getRetweetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 리트윗 취소
 */
export const undoRetweet = (
    id: number, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<UndoRetweet200>> => {
    
    return axios.delete(
      `https://api.ugot.uk/api/tweets/${id}/retweet`,options
    );
  }



export const getUndoRetweetMutationOptions = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof undoRetweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof undoRetweet>>, TError,{id: number}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof undoRetweet>>, {id: number}> = (props) => {
          const {id} = props ?? {};

          return  undoRetweet(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type UndoRetweetMutationResult = NonNullable<Awaited<ReturnType<typeof undoRetweet>>>
    
    export type UndoRetweetMutationError = AxiosError<unknown>

    /**
 * @summary 리트윗 취소
 */
export const useUndoRetweet = <TError = AxiosError<unknown>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof undoRetweet>>, TError,{id: number}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof undoRetweet>>,
        TError,
        {id: number},
        TContext
      > => {

      const mutationOptions = getUndoRetweetMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * 특정 트윗의 조회수를 1 증가시킵니다. 동일 세션에서 중복 호출은 무시됩니다.
 * @summary 트윗 조회수 증가
 */
export const incrementTweetView = (
    id: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<IncrementTweetView200>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/tweets/${id}/views`,undefined,options
    );
  }



export const getIncrementTweetViewMutationOptions = <TError = AxiosError<IncrementTweetView400 | IncrementTweetView401 | IncrementTweetView404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof incrementTweetView>>, TError,{id: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof incrementTweetView>>, TError,{id: string}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof incrementTweetView>>, {id: string}> = (props) => {
          const {id} = props ?? {};

          return  incrementTweetView(id,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type IncrementTweetViewMutationResult = NonNullable<Awaited<ReturnType<typeof incrementTweetView>>>
    
    export type IncrementTweetViewMutationError = AxiosError<IncrementTweetView400 | IncrementTweetView401 | IncrementTweetView404>

    /**
 * @summary 트윗 조회수 증가
 */
export const useIncrementTweetView = <TError = AxiosError<IncrementTweetView400 | IncrementTweetView401 | IncrementTweetView404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof incrementTweetView>>, TError,{id: string}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof incrementTweetView>>,
        TError,
        {id: string},
        TContext
      > => {

      const mutationOptions = getIncrementTweetViewMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 투표 생성 (독립적 생성 또는 트윗과 연결)
 */
export const createPoll = (
    createPollBody: CreatePollBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<CreatePoll201>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/tweets/poll`,
      createPollBody,options
    );
  }



export const getCreatePollMutationOptions = <TError = AxiosError<CreatePoll400 | CreatePoll401>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPoll>>, TError,{data: CreatePollBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof createPoll>>, TError,{data: CreatePollBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPoll>>, {data: CreatePollBody}> = (props) => {
          const {data} = props ?? {};

          return  createPoll(data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type CreatePollMutationResult = NonNullable<Awaited<ReturnType<typeof createPoll>>>
    export type CreatePollMutationBody = CreatePollBody
    export type CreatePollMutationError = AxiosError<CreatePoll400 | CreatePoll401>

    /**
 * @summary 투표 생성 (독립적 생성 또는 트윗과 연결)
 */
export const useCreatePoll = <TError = AxiosError<CreatePoll400 | CreatePoll401>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPoll>>, TError,{data: CreatePollBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof createPoll>>,
        TError,
        {data: CreatePollBody},
        TContext
      > => {

      const mutationOptions = getCreatePollMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 투표하기
 */
export const voteOnPoll = (
    id: string,
    voteOnPollBody: VoteOnPollBody, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<VoteOnPoll200>> => {
    
    return axios.post(
      `https://api.ugot.uk/api/tweets/${id}/poll/vote`,
      voteOnPollBody,options
    );
  }



export const getVoteOnPollMutationOptions = <TError = AxiosError<VoteOnPoll400 | VoteOnPoll401 | VoteOnPoll403 | VoteOnPoll404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof voteOnPoll>>, TError,{id: string;data: VoteOnPollBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationOptions<Awaited<ReturnType<typeof voteOnPoll>>, TError,{id: string;data: VoteOnPollBody}, TContext> => {
const {mutation: mutationOptions, axios: axiosOptions} = options ?? {};

      


      const mutationFn: MutationFunction<Awaited<ReturnType<typeof voteOnPoll>>, {id: string;data: VoteOnPollBody}> = (props) => {
          const {id,data} = props ?? {};

          return  voteOnPoll(id,data,axiosOptions)
        }

        


  return  { mutationFn, ...mutationOptions }}

    export type VoteOnPollMutationResult = NonNullable<Awaited<ReturnType<typeof voteOnPoll>>>
    export type VoteOnPollMutationBody = VoteOnPollBody
    export type VoteOnPollMutationError = AxiosError<VoteOnPoll400 | VoteOnPoll401 | VoteOnPoll403 | VoteOnPoll404>

    /**
 * @summary 투표하기
 */
export const useVoteOnPoll = <TError = AxiosError<VoteOnPoll400 | VoteOnPoll401 | VoteOnPoll403 | VoteOnPoll404>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof voteOnPoll>>, TError,{id: string;data: VoteOnPollBody}, TContext>, axios?: AxiosRequestConfig}
): UseMutationResult<
        Awaited<ReturnType<typeof voteOnPoll>>,
        TError,
        {id: string;data: VoteOnPollBody},
        TContext
      > => {

      const mutationOptions = getVoteOnPollMutationOptions(options);

      return useMutation(mutationOptions);
    }
    /**
 * @summary 투표 결과 조회
 */
export const getPollResults = (
    id: string, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetPollResults200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/tweets/${id}/poll/results`,options
    );
  }


export const getGetPollResultsQueryKey = (id: string,) => {
    return [`https://api.ugot.uk/api/tweets/${id}/poll/results`] as const;
    }

    
export const getGetPollResultsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getPollResults>>, TError = AxiosError<GetPollResults404>>(id: string, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getPollResults>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPollResultsQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPollResults>>> = ({ signal }) => getPollResults(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getPollResults>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPollResultsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getPollResults>>>
export type GetPollResultsInfiniteQueryError = AxiosError<GetPollResults404>

/**
 * @summary 투표 결과 조회
 */
export const useGetPollResultsInfinite = <TData = Awaited<ReturnType<typeof getPollResults>>, TError = AxiosError<GetPollResults404>>(
 id: string, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getPollResults>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetPollResultsInfiniteQueryOptions(id,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetPollResultsQueryOptions = <TData = Awaited<ReturnType<typeof getPollResults>>, TError = AxiosError<GetPollResults404>>(id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPollResults>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPollResultsQueryKey(id);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPollResults>>> = ({ signal }) => getPollResults(id, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(id),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPollResults>>, TError, TData> & { queryKey: QueryKey }
}

export type GetPollResultsQueryResult = NonNullable<Awaited<ReturnType<typeof getPollResults>>>
export type GetPollResultsQueryError = AxiosError<GetPollResults404>

/**
 * @summary 투표 결과 조회
 */
export const useGetPollResults = <TData = Awaited<ReturnType<typeof getPollResults>>, TError = AxiosError<GetPollResults404>>(
 id: string, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getPollResults>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetPollResultsQueryOptions(id,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 해시태그 목록을 조건에 따라 조회합니다. 검색 조건 없이 호출할 경우 인기 해시태그 목록을 조회합니다.
 * @summary 해시태그 목록 조회
 */
export const getHashtags = (
    params?: GetHashtagsParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetHashtags200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/hashtags`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetHashtagsQueryKey = (params?: GetHashtagsParams,) => {
    return [`https://api.ugot.uk/api/hashtags`, ...(params ? [params]: [])] as const;
    }

    
export const getGetHashtagsInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getHashtags>>, TError = AxiosError<unknown>>(params?: GetHashtagsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getHashtags>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHashtagsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHashtags>>> = ({ signal }) => getHashtags(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getHashtags>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHashtagsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getHashtags>>>
export type GetHashtagsInfiniteQueryError = AxiosError<unknown>

/**
 * @summary 해시태그 목록 조회
 */
export const useGetHashtagsInfinite = <TData = Awaited<ReturnType<typeof getHashtags>>, TError = AxiosError<unknown>>(
 params?: GetHashtagsParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getHashtags>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetHashtagsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetHashtagsQueryOptions = <TData = Awaited<ReturnType<typeof getHashtags>>, TError = AxiosError<unknown>>(params?: GetHashtagsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHashtags>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHashtagsQueryKey(params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHashtags>>> = ({ signal }) => getHashtags(params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn,   staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHashtags>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHashtagsQueryResult = NonNullable<Awaited<ReturnType<typeof getHashtags>>>
export type GetHashtagsQueryError = AxiosError<unknown>

/**
 * @summary 해시태그 목록 조회
 */
export const useGetHashtags = <TData = Awaited<ReturnType<typeof getHashtags>>, TError = AxiosError<unknown>>(
 params?: GetHashtagsParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHashtags>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetHashtagsQueryOptions(params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



/**
 * 특정 해시태그의 정보와 관련 트윗을 조회합니다.
 * @summary 특정 해시태그 조회
 */
export const getHashtagByTag = (
    tag: string,
    params?: GetHashtagByTagParams, options?: AxiosRequestConfig
 ): Promise<AxiosResponse<GetHashtagByTag200>> => {
    
    return axios.get(
      `https://api.ugot.uk/api/hashtags/${tag}`,{
    ...options,
        params: {...params, ...options?.params},}
    );
  }


export const getGetHashtagByTagQueryKey = (tag: string,
    params?: GetHashtagByTagParams,) => {
    return [`https://api.ugot.uk/api/hashtags/${tag}`, ...(params ? [params]: [])] as const;
    }

    
export const getGetHashtagByTagInfiniteQueryOptions = <TData = Awaited<ReturnType<typeof getHashtagByTag>>, TError = AxiosError<GetHashtagByTag404>>(tag: string,
    params?: GetHashtagByTagParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getHashtagByTag>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHashtagByTagQueryKey(tag,params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHashtagByTag>>> = ({ signal }) => getHashtagByTag(tag,params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(tag),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof getHashtagByTag>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHashtagByTagInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof getHashtagByTag>>>
export type GetHashtagByTagInfiniteQueryError = AxiosError<GetHashtagByTag404>

/**
 * @summary 특정 해시태그 조회
 */
export const useGetHashtagByTagInfinite = <TData = Awaited<ReturnType<typeof getHashtagByTag>>, TError = AxiosError<GetHashtagByTag404>>(
 tag: string,
    params?: GetHashtagByTagParams, options?: { query?:UseInfiniteQueryOptions<Awaited<ReturnType<typeof getHashtagByTag>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetHashtagByTagInfiniteQueryOptions(tag,params,options)

  const query = useInfiniteQuery(queryOptions) as  UseInfiniteQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



export const getGetHashtagByTagQueryOptions = <TData = Awaited<ReturnType<typeof getHashtagByTag>>, TError = AxiosError<GetHashtagByTag404>>(tag: string,
    params?: GetHashtagByTagParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHashtagByTag>>, TError, TData>, axios?: AxiosRequestConfig}
) => {

const {query: queryOptions, axios: axiosOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHashtagByTagQueryKey(tag,params);

  

    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHashtagByTag>>> = ({ signal }) => getHashtagByTag(tag,params, { signal, ...axiosOptions });

      

      

   return  { queryKey, queryFn, enabled: !!(tag),  staleTime: 300000, gcTime: 1800000,  ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHashtagByTag>>, TError, TData> & { queryKey: QueryKey }
}

export type GetHashtagByTagQueryResult = NonNullable<Awaited<ReturnType<typeof getHashtagByTag>>>
export type GetHashtagByTagQueryError = AxiosError<GetHashtagByTag404>

/**
 * @summary 특정 해시태그 조회
 */
export const useGetHashtagByTag = <TData = Awaited<ReturnType<typeof getHashtagByTag>>, TError = AxiosError<GetHashtagByTag404>>(
 tag: string,
    params?: GetHashtagByTagParams, options?: { query?:UseQueryOptions<Awaited<ReturnType<typeof getHashtagByTag>>, TError, TData>, axios?: AxiosRequestConfig}

  ):  UseQueryResult<TData, TError> & { queryKey: QueryKey } => {

  const queryOptions = getGetHashtagByTagQueryOptions(tag,params,options)

  const query = useQuery(queryOptions) as  UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey ;

  return query;
}



